import { api } from '@/lib/api';
import type { ActiveProvider, InternalTargetField, MappingFormData, Provider } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface Mapping {
  id: string;
  provider: Provider;
  sourceFieldId: string;
  internalTarget: InternalTargetField;
  createdAt: string;
}

export function useMappingsByProvider(provider?: ActiveProvider) {
  return useQuery({
    queryKey: ['mappings', 'provider', provider],
    queryFn: () => api.get<Mapping[]>(`/mappings/provider/${provider}`),
    enabled: !!provider,
  });
}

export function useMapping(id: string) {
  return useQuery({
    queryKey: ['mappings', id],
    queryFn: () => api.get<Mapping>(`/mappings/${id}`),
    enabled: !!id,
  });
}

export function useCreateMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MappingFormData) => api.post<Mapping>('/mappings', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mappings', 'provider', variables.provider] });
      toast.success('Mapping aangemaakt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij aanmaken mapping: ${error.message}`);
    },
  });
}

export function useUpdateMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MappingFormData> }) =>
      api.put<Mapping>(`/mappings/${id}`, data),
    onSuccess: mapping => {
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      queryClient.invalidateQueries({ queryKey: ['mappings', mapping.id] });
      queryClient.invalidateQueries({ queryKey: ['mappings', 'provider', mapping.provider] });
      toast.success('Mapping bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken mapping: ${error.message}`);
    },
  });
}

export function useDeleteMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/mappings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      toast.success('Mapping verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen mapping: ${error.message}`);
    },
  });
}

export function useBulkUpsertMappings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ provider, mappings }: { provider: ActiveProvider; mappings: MappingFormData[] }) =>
      api.post<Mapping[]>(`/mappings/provider/${provider}/bulk`, mappings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mappings', 'provider', variables.provider] });
      toast.success('Mappings opgeslagen');
    },
    onError: (error: unknown) => {
      console.error('Bulk upsert error:', error);
      const message = error instanceof Error ? error.message : JSON.stringify(error);
      toast.error(`Fout bij opslaan mappings: ${message}`);
    },
  });
}
